import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AnnieChatItem } from "@/hooks/useAnnie";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { FONT_SIZE, INK } from "@/theme/tokens";
import { ChatCard } from "@/ui/Chat/ChatCard";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { parseTimeString } from "@/utils/dateUtils";
import { TimeRangeSelection } from "../EditTimePopover/EditTimePopover";

type AnnieClarificationItem = Extract<AnnieChatItem, { kind: "clarification" }>;

type Props = {
	item: AnnieClarificationItem;
	onSubmitTimes: (values: { start_time: string; end_time: string }) => void;
};

// DEFAULT: a time_range clarification always ships `defaults` per the backend contract; this 7a–7p
// fallback only guards a malformed card so the picker can still render (the backend re-validates).
const FALLBACK_DEFAULTS = { start_time: "07:00", end_time: "19:00" } as const;

/**
 * A time clarification from Annie — asked when the head nurse never stated shift hours, so Annie
 * offers a pre-filled start/end editor instead of inventing them. Reuses the app's shift time
 * picker (am/pm display), seeds from `card.defaults`, and submits 24-hour "HH:MM". The chip-based
 * sibling renders in AnnieClarificationCard; this handles `clarificationKind === "time_range"`.
 */
export const AnnieTimeRangeCard: FC<Props> = ({ item, onSubmitTimes }) => {
	const { t } = useTranslation();
	const { timezone } = useDepartmentFlags();
	const { card, isChoosing, isExpired } = item;

	const seed = useMemo(() => {
		const defaults = card.defaults ?? FALLBACK_DEFAULTS;
		const base = DateTime.now().setZone(timezone).startOf("day");
		const start = base.set(parseTimeString(defaults.start_time));
		let end = base.set(parseTimeString(defaults.end_time));
		// An overnight range (end at/before start) rolls to the next day so the picker reads a forward span.
		if (end <= start) end = end.plus({ days: 1 });
		return { start, end };
	}, [card.defaults, timezone]);

	const [selectedStart, setSelectedStart] = useState<DateTime>(seed.start);
	const [selectedEnd, setSelectedEnd] = useState<DateTime>(seed.end);

	const isValid = selectedEnd.toMillis() !== selectedStart.toMillis();

	const handleConfirm = () => {
		if (!isValid || isChoosing || isExpired) return;
		onSubmitTimes({
			start_time: selectedStart.setZone(timezone).toFormat("HH:mm"),
			end_time: selectedEnd.setZone(timezone).toFormat("HH:mm"),
		});
	};

	return (
		<ChatCard dataTestId={`annie-clarification-card-${card.clarification_id}`} dimmed={isExpired}>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: 1.5, color: INK.PRIMARY }}>{card.question}</Typography>
			<TimeRangeSelection
				initStartTimeWDefault={seed.start}
				selectedStart={selectedStart}
				setSelectedStart={setSelectedStart}
				selectedEnd={selectedEnd}
				setSelectedEnd={setSelectedEnd}
				disabled={isChoosing || isExpired}
				startLabel={t("annie.times.start")}
				endLabel={t("annie.times.end")}
				startDataTestId={`annie-times-start-${card.clarification_id}`}
				endDataTestId={`annie-times-end-${card.clarification_id}`}
				error={false}
				sx={null}
			/>
			{!isValid && <Typography sx={(theme) => ({ fontSize: FONT_SIZE.SECONDARY, color: theme.palette.error.main })}>{t("annie.times.invalid")}</Typography>}
			<Box sx={{ display: "flex" }}>
				<SystemButton
					buttonVariant="primary"
					size="small"
					loading={isChoosing}
					disabled={!isValid || isExpired}
					onClick={handleConfirm}
					data-testid={`annie-times-confirm-${card.clarification_id}`}
				>
					{t("annie.times.confirm")}
				</SystemButton>
			</Box>
		</ChatCard>
	);
};
